import React, { ReactNode, useEffect } from "react";

import { CssBaseline, ThemeProvider } from "@mui/material";

import { darkTheme } from "@themes/darkTheme";
import { lightTheme } from "@themes/lightTheme";

export enum ThemeMode {
  Light,
  Dark,
}

export interface AppContextInterface {
  theme: ThemeMode;
  systemTheme: ThemeMode;
  updateTheme: (t: ThemeMode) => void;
}

export const AppContext = React.createContext<AppContextInterface>({
  theme: ThemeMode.Light,
  systemTheme: ThemeMode.Light,
  updateTheme: () => {},
});

const COLOR_MODE_KEY = "color-mode";

function getSystemTheme() {
  if (typeof window !== "undefined") {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    return mql.matches ? ThemeMode.Dark : ThemeMode.Light;
  } else {
    return ThemeMode.Light;
  }
}

function getThemeMode() {
  if (typeof window !== "undefined") {
    const persistedColorPreference =
      window.localStorage.getItem(COLOR_MODE_KEY);
    if (typeof persistedColorPreference === "string") {
      return persistedColorPreference === "light"
        ? ThemeMode.Light
        : ThemeMode.Dark;
    } else {
      return getSystemTheme();
    }
  } else {
    return ThemeMode.Light;
  }
}

interface AppContextProviderProps {
  children: ReactNode;
}

export const AppContextProvider: React.FunctionComponent<
  AppContextProviderProps
> = ({ children }: AppContextProviderProps) => {
  const [theme, updateTheme] = React.useState<ThemeMode>(ThemeMode.Light);
  const [systemTheme, updateSystemTheme] = React.useState<ThemeMode>(
    ThemeMode.Light,
  );

  useEffect(() => {
    updateTheme(getThemeMode());
    updateSystemTheme(getSystemTheme());
  }, []);

  const updateAndSave = (mode: ThemeMode) => {
    window.localStorage.setItem(
      COLOR_MODE_KEY,
      mode === ThemeMode.Light ? "light" : "dark",
    );
    updateTheme(mode);
  };

  return (
    <>
      <AppContext.Provider
        value={{ theme, updateTheme: updateAndSave, systemTheme }}
      >
        <ThemeProvider
          theme={theme === ThemeMode.Light ? lightTheme : darkTheme}
        >
          <CssBaseline />
          {children}
        </ThemeProvider>
      </AppContext.Provider>
    </>
  );
};
